import { type ReactNode } from 'react';

import type {
  TRenderSelectedValuesCtx,
  TFilterConfig,
} from '../../types/filter-config';

import {
  Filter,
  FilterClearBtn,
  FilterLabel,
  FilterValue,
  FilterOptionsList,
  FilterTrigger,
  FilterPlaceholder,
} from '@src/design-system/filter-new';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { useTableViewHelpers } from '@design-system/molecules/ticlick-table/hooks/use-table-view-helpers';
import { ConditionallyRenderedFilterPanel } from './conditionally-rendered-filter-panel';
import { FILTER_KIND } from '../../types/filter-config';
import {
  FILTER_NONE_LABEL,
  isNoneFilterValue,
} from '../../helpers/filter-none';

export function InputFilterTrigger<T extends Record<string, unknown>>({
  filterLabel,
  renderValues,
  config,
  disabled = false,
  hint,
}: {
  filterLabel: string;
  renderValues?: (ctx: TRenderSelectedValuesCtx) => ReactNode;
  config: TFilterConfig<T>;
  disabled?: boolean;
  hint?: string;
}) {
  const { clearMultipleFacets, getSelectedStaticFilterFacet } =
    useTableViewHelpers();

  if (!config) return null;

  const facet = getSelectedStaticFilterFacet(config.facetName);
  const selectedValues = facet?.value || [];
  const selectedFriendlyValues = facet?.friendlyValue || [];

  const hasFriendlyValues = !!selectedFriendlyValues.length;
  const hasValues = !!selectedValues.length;
  const isNoneSelected = isNoneFilterValue(selectedValues);
  const includesNone = selectedValues.includes(null);

  const labelFor = (value: string | number | null) => {
    if (value === null) return FILTER_NONE_LABEL;
    if (config.kind === FILTER_KIND.simple) {
      return (
        config.options.find((option) => option.value === String(value))
          ?.label ?? formatTextForDisplay(String(value))
      );
    }
    return formatTextForDisplay(String(value));
  };

  return (
    <Filter {...(disabled ? { open: false } : {})}>
      <div className='w-full flex items-center justify-between'>
        <FilterLabel>{filterLabel}</FilterLabel>

        {hasValues && (
          <FilterClearBtn
            onClearClick={() => clearMultipleFacets([config.facetName])}
          />
        )}
      </div>
      <FilterTrigger
        disabled={disabled}
        className={disabled ? 'cursor-not-allowed opacity-60' : undefined}
      >
        {hasValues && facet ? (
          isNoneSelected ? (
            <FilterValue>{FILTER_NONE_LABEL}</FilterValue>
          ) : (
            <div className='flex items-center flex-wrap gap-1'>
              {renderValues?.({ filter: facet }) ??
                (hasFriendlyValues
                  ? selectedFriendlyValues
                  : selectedValues
                )?.map((g) => <FilterValue key={g}>{labelFor(g)}</FilterValue>)}
              {includesNone && renderValues ? (
                <FilterValue>{FILTER_NONE_LABEL}</FilterValue>
              ) : null}
            </div>
          )
        ) : (
          <FilterPlaceholder>Select</FilterPlaceholder>
        )}
      </FilterTrigger>

      <FilterOptionsList
        align='end'
        className='p-0 w-auto'
        onClick={(e) => e.stopPropagation()}
      >
        <ConditionallyRenderedFilterPanel config={config} />
      </FilterOptionsList>
      {hint ? <p className='mt-1 text-xs text-gray-600'>{hint}</p> : null}
    </Filter>
  );
}
